import type { ReactNode } from "react";
import { Hand, Smartphone, Wifi } from "lucide-react";
import type { SlideProps } from "@/engine/types";
import { chapterMeta } from "@/engine/chapters";
import { Reveal, RevealItem, RevealList, SlideShell } from "@/ui";
import { TopicCard } from "./components/TopicCard";
import type { Topic } from "./components/TopicCard";

const [FROM, TO] = chapterMeta(4).accent;

/** Semibold-white emphasis (source deck's **bold**). */
function B({ children }: { children: ReactNode }) {
  return <span className="font-semibold text-white">{children}</span>;
}

const TOPICS: readonly Topic[] = [
  {
    icon: Smartphone,
    color: FROM,
    title: "Mobile-first indexing",
    lines: [
      <>
        Google <B>predominantly</B> uses the <B>mobile version</B>
      </>,
      <>
        <B>Parity matters</B> — no hiding critical text on mobile
      </>,
    ],
  },
  {
    icon: Hand,
    color: TO,
    title: "Layout & touch targets",
    lines: [
      <>They affect engagement</>,
      <>
        <B>Slow or jumpy</B> UIs lose users before SEO even matters
      </>,
    ],
  },
  {
    icon: Wifi,
    color: "#34d399",
    title: "Test like your users",
    lines: [
      <>
        On <B>real devices</B> and <B>throttled</B> networks
      </>,
      <>
        <B>Same URLs</B> with responsive design usually beat separate m-dot sites
      </>,
    ],
  },
];

export function MobileSeoSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={4} title="Mobile SEO">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-12">
        <RevealList active={active} stagger={0.16} className="grid grid-cols-3 gap-8">
          {TOPICS.map((topic) => (
            <RevealItem key={topic.title} className="h-full">
              <TopicCard topic={topic} />
            </RevealItem>
          ))}
        </RevealList>

        {/* Responsive vs m-dot — invented example domains */}
        <Reveal active={active} delay={0.9}>
          <div className="flex items-center justify-center gap-6">
            <span className="rounded-xl border border-accent-emerald/35 bg-accent-emerald/10 px-6 py-3 font-mono text-xl text-accent-emerald">
              yoursite.example · responsive, same URLs
            </span>
            <span className="text-2xl text-white/70">usually beats</span>
            <span className="rounded-xl border border-accent-rose/30 bg-accent-rose/10 px-6 py-3 font-mono text-xl text-accent-rose/80 line-through decoration-2">
              m.yoursite.example
            </span>
          </div>
        </Reveal>
      </div>
    </SlideShell>
  );
}
